import axios, { AxiosResponse, AxiosError } from 'axios';
import {
  User,
  CreateUserData,
  LoginCredentials,
  AuthResponse,
  Patient,
  CreatePatientData,
  Assureur,
  RendezVous,
  CreateRendezVousData,
  Consultation,
  TreatmentType,
  CreateTreatmentTypeData,
  ConfigRappelTraitement,
  CreateConfigRappelData,
  Doctor,
  Facture,
  Intrant,
  MouvementStock,
  PaginatedResponse,
  QueryParams,
  DisponibiliteDoctor,
  Fournisseur,
  CategorieIntrant,
  CreateCategorieIntrantData,
  LotIntrant,
  Paiement,
  DashboardStats,
  PatientStats,
  FinancialStats,
  TreatmentStats,
  CrossAnalysisStats,
  ExecutiveKPIs,
  Medicament,
  MessageTemplate,
  CommunicationLog,
  NotificationPreference,
  SendMessageData,
  BulkMessageData,
  CommunicationStats,
  // Prosthesis types
  Prestataire,
  TypeProthese,
  CommandeProthese,
  CommandeProtheseList,
  SuiviLivraison,
  NotificationProthese,
  CreateCommandeProtheseData,
  CreateSuiviLivraisonData,
  PrestataireStats,
  DashboardProthesisStats,
  Treatment,
  // Depenses types
  CategorieDepense,
  Depense,
  DepenseList,
  CreateDepenseData,
  BudgetMensuel,
  PaiementFournisseurDepense,
  DepenseStatistiques,
  PayerDepenseData,
  // Proforma types
  FactureProforma,
  FactureProformaList,
  CreateProformaData,
  ConvertProformaData,
  ProformaStatistiques,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Fonction pour récupérer le token CSRF depuis les cookies
const getCsrfToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Fonction pour obtenir le token CSRF depuis le serveur
export const fetchCsrfToken = async (): Promise<void> => {
  try {
    await api.get('/auth/csrf/');
  } catch (error) {
    console.error('Erreur lors de la récupération du token CSRF:', error);
  }
};

// Intercepteur pour ajouter le token d'authentification - Development only
/* api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
); */

// Interceptor pour ajouter le token CSRF
api.interceptors.request.use(
  (config) => {
    const token = getCsrfToken();
    if (token) {
      config.headers['X-CSRFToken'] = token;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs - development only
/* api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
); */

// Interceptor pour gérer les réponses
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF')) {
      console.log('Erreur CSRF - tentative de rechargement du token');
      // Recharger la page pour récupérer un nouveau token CSRF
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

export default api;

// Services spécifiques
export const authService = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login/', credentials),
  
  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/auth/logout/'),
  
  getProfile: (): Promise<AxiosResponse<User>> =>
    api.get('/auth/me/'),
};

export const patientService = {
  getAll: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Patient>>> =>
    api.get('/patients/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Patient>> =>
    api.get(`/patients/${id}/`),
  
  create: (data: CreatePatientData): Promise<AxiosResponse<Patient>> =>
    api.post('/patients/', data),
  
  update: (id: number, data: Partial<CreatePatientData>): Promise<AxiosResponse<Patient>> =>
    api.put(`/patients/${id}/`, data),
  
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/patients/${id}/`),
  
  uploadCarteAssurance: (id: number, photo: File): Promise<AxiosResponse<{ message: string }>> => {
    const formData = new FormData();
    formData.append('photo', photo);
    return api.post(`/patients/${id}/upload_carte_assurance/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getCompteTiers: (id: number): Promise<AxiosResponse<{
    patient: { id: number; nom: string; prenoms: string };
    solde: string | number;
    total_paye: string | number;
    balance_agee: { '0_30': string | number; '31_60': string | number; '61_90': string | number; 'plus_90': string | number; total: string | number };
    factures: Array<{
      id: number; numero: string; date_emission: string; date_echeance: string;
      montant_patient: string | number; montant_paye: string | number; reste_a_payer: string | number; status: string;
    }>;
  }>> =>
    api.get(`/patients/${id}/compte_tiers/`),

  // Groupes de patients actifs ayant même nom, prénoms et téléphone
  getDoublons: (): Promise<AxiosResponse<PatientDoublon[][]>> =>
    api.get('/patients/doublons/'),

  // Rattache tout le dossier des `doublons` au patient `id`, puis les désactive
  fusionner: (id: number, doublons: number[]): Promise<AxiosResponse<{ patient: Patient; deplaces: Record<string, number> }>> =>
    api.post(`/patients/${id}/fusionner/`, { doublons }),
};

export interface PatientDoublon {
  id: number;
  nom: string;
  prenoms: string;
  telephone: string;
  date_naissance: string;
  created_at: string;
  assureur_nom: string | null;
  nb_consultations: number;
  nb_factures: number;
  nb_rendez_vous: number;
}

export const assureurService = {
  getAll: (): Promise<AxiosResponse<PaginatedResponse<Assureur>>> =>
    api.get('/assureurs/'),
  
  create: (data: Omit<Assureur, 'id' | 'created_at'>): Promise<AxiosResponse<Assureur>> =>
    api.post('/assureurs/', data),
};

export const planningService = {
  getRendezVous: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<RendezVous>>> =>
    api.get('/rendez-vous/', { params }),
  
  createRendezVous: (data: CreateRendezVousData): Promise<AxiosResponse<RendezVous>> =>
    api.post('/rendez-vous/', data),
  
  updateRendezVous: (id: number, data: Partial<CreateRendezVousData>): Promise<AxiosResponse<RendezVous>> =>
    api.put(`/rendez-vous/${id}/`, data),
  
  deleteRendezVous: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/rendez-vous/${id}/`),

  getAgendaSemaine: (): Promise<AxiosResponse<RendezVous[]>> =>
    api.get('/rendez-vous/agenda_semaine/'),

  getAujourdhui: (): Promise<AxiosResponse<RendezVous[]>> =>
    api.get('/rendez-vous/aujourd_hui/'),

  confirmerRdv: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/rendez-vous/${id}/confirmer/`),

  annulerRdv: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/rendez-vous/${id}/annuler/`),

  changerStatutRdv: (id: number, status: string): Promise<AxiosResponse<{ message: string; status: string }>> =>
    api.post(`/rendez-vous/${id}/changer_statut/`, { status }),

  getDisponibilites: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<DisponibiliteDoctor>>> =>
    api.get('/disponibilites/', { params }),

  // Note: Cet endpoint n'a pas de pagination (retourne directement un tableau)
  getDoctors: (): Promise<AxiosResponse<Doctor[]>> =>
    api.get('/treatments/doctors/'),
};

export const consultationService = {
  getAll: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Consultation>>> =>
    api.get('/consultations/', { params }),

  getByPatient: (patientId: number): Promise<AxiosResponse<PaginatedResponse<Consultation>>> =>
    api.get('/consultations/', { params: { patient: patientId } }),

  create: (data: Partial<Consultation>): Promise<AxiosResponse<Consultation>> =>
    api.post('/consultations/', data),

  getById: (id: number): Promise<AxiosResponse<Consultation>> =>
    api.get(`/consultations/${id}/`),

  update: (id: number, data: Partial<Consultation>): Promise<AxiosResponse<Consultation>> =>
    api.put(`/consultations/${id}/`, data),

  getAujourdhui: (): Promise<AxiosResponse<Consultation[]>> =>
    api.get('/consultations/aujourd_hui/'),

  // Note: Ces endpoints n'ont pas de pagination (retournent directement un tableau)
  getTreatmentTypes: (params?: QueryParams): Promise<AxiosResponse<TreatmentType[]>> =>
    api.get('/treatments/treatment-types/', { params }),

  getDoctors: (params?: QueryParams): Promise<AxiosResponse<Doctor[]>> =>
    api.get('/treatments/doctors/', { params }),

  ajouterActe: (consultationId: number, data: any): Promise<AxiosResponse<any>> =>
    api.post(`/consultations/${consultationId}/ajouter_acte/`, data),

  imprimerOrdonnance: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/consultations/${id}/imprimer_ordonnance/`, { responseType: 'blob' }),

  ajouterOrdonnance: (consultationId: number, data: {
    contenu?: string;
    notes?: string;
    medicaments: Array<{
      medicament_id: number;
      dosage: string;
      frequence: string;
      duree: string;
      instructions?: string;
      quantite?: string;
    }>;
  }): Promise<AxiosResponse<any>> =>
    api.post(`/consultations/${consultationId}/ajouter_ordonnance/`, data),

  // Suite de soins : verrouille la consultation sans bon ni facture
  cloturerSansFacturation: (id: number): Promise<AxiosResponse<Consultation>> =>
    api.post(`/consultations/${id}/cloturer_sans_facturation/`),

  deleteConsultation: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/consultations/${id}/`),
};

export const medicamentService = {
  getAll: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Medicament>>> =>
    api.get('/medicaments/', { params }),
  
  getById: (id: number): Promise<AxiosResponse<Medicament>> =>
    api.get(`/medicaments/${id}/`),

  // Création rapide depuis l'ordonnance : renvoie la fiche existante si même nom + dosage
  create: (data: { nom: string; dosage_standard: string }): Promise<AxiosResponse<Medicament>> =>
    api.post('/medicaments/', data),
};

export const facturationService = {
  getFactures: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Facture>>> =>
    api.get('/factures/', { params }),
  
  createFacture: (data: Partial<Facture>): Promise<AxiosResponse<Facture>> =>
    api.post('/factures/', data),

  updateFacture: (id: number, data: Partial<Facture>): Promise<AxiosResponse<Facture>> =>
    api.patch(`/factures/${id}/`, data),

  getFactureById: (id: number): Promise<AxiosResponse<Facture>> =>
    api.get(`/factures/${id}/`),

  getImpayees: (): Promise<AxiosResponse<Facture[]>> =>
    api.get('/factures/impayees/'),

  getStatistiques: (): Promise<AxiosResponse<any>> =>
    api.get('/factures/statistiques/'),

  ajouterPaiement: (factureId: number, data: Partial<Paiement>): Promise<AxiosResponse<Paiement>> =>
    api.post(`/factures/${factureId}/ajouter_paiement/`, data),

  imprimerFacture: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/factures/${id}/imprimer/`, { responseType: 'blob' }),

  // Reçu de paiement : couvre tous les règlements de la facture
  imprimerRecu: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/factures/${id}/recu/`, { responseType: 'blob' }),

  // Reçu d'un règlement précis
  imprimerRecuPaiement: (paiementId: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/paiements/${paiementId}/recu/`, { responseType: 'blob' }),

  getModesPaiement: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/factures/modes_paiement/'),

  deleteFacture: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/factures/${id}/`),
};

export const stockService = {
  updateIntrant: (id: number, data: Partial<Intrant>): Promise<AxiosResponse<Intrant>> =>
    api.put(`/intrants/${id}/`, data),
  getIntrants: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Intrant>>> =>
    api.get('/intrants/', { params }),
  
  createIntrant: (data: Partial<Intrant>): Promise<AxiosResponse<Intrant>> =>
    api.post('/intrants/', data),
  
  getMouvements: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<MouvementStock>>> =>
    api.get('/mouvements-stock/', { params }),
  
  createMouvement: (data: Partial<MouvementStock>): Promise<AxiosResponse<MouvementStock>> =>
    api.post('/mouvements-stock/', data),

  getStockCritique: (): Promise<AxiosResponse<Intrant[]>> =>
    api.get('/intrants/stock_critique/'),

  getPeremptionProche: (): Promise<AxiosResponse<LotIntrant[]>> =>
    api.get('/intrants/peremption_proche/'),

  getStatistiques: (): Promise<AxiosResponse<any>> =>
    api.get('/intrants/statistiques/'),

  getValorisation: (): Promise<AxiosResponse<{
    valeur_totale: string | number;
    par_categorie: Array<{ categorie: string; valeur: string | number }>;
    intrants: Array<{
      id: number; code: string; nom: string; categorie: string | null; unite: string;
      stock_actuel: number; cout_moyen_pondere: string | number; valeur_stock: string | number;
    }>;
  }>> =>
    api.get('/intrants/valorisation/'),

  getFournisseurs: (): Promise<AxiosResponse<PaginatedResponse<Fournisseur>>> =>
    api.get('/fournisseurs/'),

  getFournisseurCompte: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/fournisseurs/${id}/compte/`),

  getCategories: (): Promise<AxiosResponse<PaginatedResponse<CategorieIntrant>>> =>
    api.get('/categories-intrants/'),

  getLots: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<LotIntrant>>> =>
    api.get('/lots-intrants/', { params }),

  getUniteChoices: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/intrants/unite_choices/'),
};

export const dashboardService = {
  getStats: (): Promise<AxiosResponse<{ stats: DashboardStats; rdv_semaine: any[] }>> =>
    api.get('/dashboard/stats/'),
};

export const analyticsApi = {
  getPatientStats: async (params?: { start_date?: string; end_date?: string }): Promise<PatientStats> => {
    const response = await api.get('/dashboard/patients/', { params });
    return response.data;
  },
  
  getFinancialStats: async (params?: { start_date?: string; end_date?: string }): Promise<FinancialStats> => {
    const response = await api.get('/dashboard/financial/', { params });
    return response.data;
  },
  getTreatmentStats: async (params?: { start_date?: string; end_date?: string }): Promise<TreatmentStats> => {
    const response = await api.get('/dashboard/treatments/', { params });
    return response.data;
  },

  getCrossAnalysisStats: async (params?: { start_date?: string; end_date?: string }): Promise<CrossAnalysisStats> => {
    const response = await api.get('/dashboard/cross-analysis/', { params });
    return response.data;
  },

  getExecutiveKPIs: async (params?: { start_date?: string; end_date?: string }): Promise<ExecutiveKPIs> => {
    const response = await api.get('/dashboard/executive/', { params });
    return response.data;
  },
};

// Services d'administration
export const adminService = {
  // Gestion des utilisateurs
  getUsers: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<User>>> =>
    api.get('/admin/users/', { params }),
  
  createUser: (data: CreateUserData): Promise<AxiosResponse<User>> =>
    api.post('/admin/users/', data),
  
  updateUser: (id: number, data: Partial<CreateUserData>): Promise<AxiosResponse<User>> =>
    api.put(`/admin/users/${id}/`, data),
  
  deleteUser: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/users/${id}/`),
  
  toggleUserStatus: (id: number): Promise<AxiosResponse<User>> =>
    api.post(`/admin/users/${id}/toggle_status/`),

  // Gestion des types de traitement
  getTreatmentTypesAdmin: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<TreatmentType>>> =>
    api.get('/admin/treatment-types/', { params }),
  
  createTreatmentType: (data: CreateTreatmentTypeData): Promise<AxiosResponse<TreatmentType>> =>
    api.post('/admin/treatment-types/', data),
  
  updateTreatmentType: (id: number, data: Partial<CreateTreatmentTypeData>): Promise<AxiosResponse<TreatmentType>> =>
    api.put(`/admin/treatment-types/${id}/`, data),
  
  deleteTreatmentType: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/treatment-types/${id}/`),

  // Gestion des rappels RDV automatiques
  getConfigRappels: (): Promise<AxiosResponse<ConfigRappelTraitement[]>> =>
    api.get('/admin/config-rappels/'),

  createConfigRappel: (data: CreateConfigRappelData): Promise<AxiosResponse<ConfigRappelTraitement>> =>
    api.post('/admin/config-rappels/', data),

  updateConfigRappel: (id: number, data: Partial<CreateConfigRappelData>): Promise<AxiosResponse<ConfigRappelTraitement>> =>
    api.put(`/admin/config-rappels/${id}/`, data),

  deleteConfigRappel: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/config-rappels/${id}/`),

  // Gestion des médecins
  getDoctorsAdmin: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Doctor>>> =>
    api.get('/admin/doctors/', { params }),
  
  createDoctor: (data: any): Promise<AxiosResponse<Doctor>> =>
    api.post('/admin/doctors/', data),
  
  updateDoctor: (id: number, data: any): Promise<AxiosResponse<Doctor>> =>
    api.put(`/admin/doctors/${id}/`, data),
  
  deleteDoctor: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/doctors/${id}/`),

  // Gestion des catégories d'intrants
  getCategoriesIntrants: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<CategorieIntrant>>> =>
    api.get('/admin/categories-intrants/', { params }),
  
  createCategorieIntrant: (data: CreateCategorieIntrantData): Promise<AxiosResponse<CategorieIntrant>> =>
    api.post('/admin/categories-intrants/', data),
  
  updateCategorieIntrant: (id: number, data: Partial<CreateCategorieIntrantData>): Promise<AxiosResponse<CategorieIntrant>> =>
    api.put(`/admin/categories-intrants/${id}/`, data),
  
  deleteCategorieIntrant: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/categories-intrants/${id}/`),
  
  toggleCategorieStatus: (id: number): Promise<AxiosResponse<CategorieIntrant>> =>
    api.post(`/admin/categories-intrants/${id}/toggle_status/`),

  // Gestion des fournisseurs
  getFournisseurs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Fournisseur>>> =>
    api.get('/admin/fournisseurs/', { params }),
  
  createFournisseur: (data: Omit<Fournisseur, 'id'>): Promise<AxiosResponse<Fournisseur>> =>
    api.post('/admin/fournisseurs/', data),
  
  updateFournisseur: (id: number, data: Partial<Omit<Fournisseur, 'id'>>): Promise<AxiosResponse<Fournisseur>> =>
    api.put(`/admin/fournisseurs/${id}/`, data),
  
  deleteFournisseur: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/fournisseurs/${id}/`),
  
  toggleFournisseurStatus: (id: number): Promise<AxiosResponse<Fournisseur>> =>
    api.post(`/admin/fournisseurs/${id}/toggle_status/`),

  // Gestion des médicaments
  getMedicaments: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Medicament>>> =>
    api.get('/admin/medicaments/', { params }),
  
  createMedicament: (data: Omit<Medicament, 'id' | 'is_active' | 'created_at'>): Promise<AxiosResponse<Medicament>> =>
    api.post('/admin/medicaments/', data),
  
  updateMedicament: (id: number, data: Partial<Omit<Medicament, 'id' | 'is_active' | 'created_at'>>): Promise<AxiosResponse<Medicament>> =>
    api.put(`/admin/medicaments/${id}/`, data),
  
  deleteMedicament: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/medicaments/${id}/`),
  
  toggleMedicamentStatus: (id: number): Promise<AxiosResponse<Medicament>> =>
    api.post(`/admin/medicaments/${id}/toggle_status/`),

  // Gestion des assureurs
  getAssureurs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Assureur>>> =>
    api.get('/admin/assureurs/', { params }),
  
  createAssureur: (data: Omit<Assureur, 'id' | 'created_at'>): Promise<AxiosResponse<Assureur>> =>
    api.post('/admin/assureurs/', data),
  
  updateAssureur: (id: number, data: Partial<Omit<Assureur, 'id' | 'created_at'>>): Promise<AxiosResponse<Assureur>> =>
    api.put(`/admin/assureurs/${id}/`, data),
  
  deleteAssureur: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/assureurs/${id}/`),
};

// Services de communication
export const communicationService = {
  // Gestion des templates
  getTemplates: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<MessageTemplate>>> =>
    api.get('/communications/templates/', { params }),
  
  getTemplateById: (id: number): Promise<AxiosResponse<MessageTemplate>> =>
    api.get(`/communications/templates/${id}/`),
  
  createTemplate: (data: Partial<MessageTemplate>): Promise<AxiosResponse<MessageTemplate>> =>
    api.post('/communications/templates/', data),
  
  updateTemplate: (id: number, data: Partial<MessageTemplate>): Promise<AxiosResponse<MessageTemplate>> =>
    api.put(`/communications/templates/${id}/`, data),
  
  deleteTemplate: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/communications/templates/${id}/`),
  
  getTemplatesByType: (type: string): Promise<AxiosResponse<MessageTemplate[]>> =>
    api.get(`/communications/templates/by_type/?type=${type}`),

  // Gestion des logs de communication
  getLogs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<CommunicationLog>>> =>
    api.get('/communications/logs/', { params }),
  
  getLogById: (id: number): Promise<AxiosResponse<CommunicationLog>> =>
    api.get(`/communications/logs/${id}/`),
  
  getStats: (): Promise<AxiosResponse<CommunicationStats>> =>
    api.get('/communications/logs/stats/'),
  
  getRecentLogs: (): Promise<AxiosResponse<CommunicationLog[]>> =>
    api.get('/communications/logs/recent/'),

  // Gestion des préférences de notification
  getPreferences: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<NotificationPreference>>> =>
    api.get('/communications/preferences/', { params }),
  
  getPreferenceById: (id: number): Promise<AxiosResponse<NotificationPreference>> =>
    api.get(`/communications/preferences/${id}/`),
  
  getPreferencesByPatient: (patientId: number): Promise<AxiosResponse<NotificationPreference>> =>
    api.get(`/communications/preferences/by_patient/?patient_id=${patientId}`),
  
  createPreferences: (data: Partial<NotificationPreference>): Promise<AxiosResponse<NotificationPreference>> =>
    api.post('/communications/preferences/', data),

  updatePreferences: (id: number, data: Partial<NotificationPreference>): Promise<AxiosResponse<NotificationPreference>> =>
    api.put(`/communications/preferences/${id}/`, data),

  // Actions de communication
  sendMessage: (data: SendMessageData): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/communications/actions/send_message/', data),
  
  sendBulkMessage: (data: BulkMessageData): Promise<AxiosResponse<{ message: string; details: any }>> =>
    api.post('/communications/actions/send_bulk_message/', data),
  
  sendAppointmentReminders: (): Promise<AxiosResponse<{ message: string; details: any }>> =>
    api.post('/communications/actions/send_appointment_reminders/'),
};

// Services de logging et traçabilité
export const logsService = {
  // Statistiques générales
  getStats: (): Promise<AxiosResponse<any>> =>
    api.get('/logs/stats/'),
  
  // Logs d'activité
  getActivities: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<any>>> =>
    api.get('/logs/activities/', { params }),
  
  getActivityDetail: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/logs/activities/${id}/`),
  
  exportActivities: (params?: any): Promise<AxiosResponse<any>> =>
    api.get('/logs/activities/export/', { params }),
  
  // Logs système
  getSystemLogs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<any>>> =>
    api.get('/logs/system/', { params }),
  
  getSystemLogDetail: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/logs/system/${id}/`),
  
  // Tentatives de connexion
  getLoginAttempts: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<any>>> =>
    api.get('/logs/logins/', { params }),
  
  // Métriques de performance
  getPerformanceMetrics: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<any>>> =>
    api.get('/logs/performance/', { params }),
  
  // Maintenance
  purgeLogs: (data: { days: number }): Promise<AxiosResponse<any>> =>
    api.post('/logs/purge/', data),
};

// API Prosthesis
export const prosthesisApi = {
  // Prestataires
  getPrestataires: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Prestataire>>> =>
    api.get('/prosthesis/prestataires/', { params }),
  
  getPrestataire: (id: number): Promise<AxiosResponse<Prestataire>> =>
    api.get(`/prosthesis/prestataires/${id}/`),
  
  createPrestataire: (data: Partial<Prestataire>): Promise<AxiosResponse<Prestataire>> =>
    api.post('/prosthesis/prestataires/', data),
  
  updatePrestataire: (id: number, data: Partial<Prestataire>): Promise<AxiosResponse<Prestataire>> =>
    api.patch(`/prosthesis/prestataires/${id}/`, data),
  
  deletePrestataire: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/prosthesis/prestataires/${id}/`),
  
  getPrestataireStats: (): Promise<AxiosResponse<PrestataireStats[]>> =>
    api.get('/prosthesis/prestataires/statistiques/'),
  
  getPrestataireCommandes: (id: number): Promise<AxiosResponse<CommandeProtheseList[]>> =>
    api.get(`/prosthesis/prestataires/${id}/commandes_actives/`),

  // Types de prothèses
  getTypesProtheses: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<TypeProthese>>> =>
    api.get('/prosthesis/types-protheses/', { params }),
  
  getTypeProthese: (id: number): Promise<AxiosResponse<TypeProthese>> =>
    api.get(`/prosthesis/types-protheses/${id}/`),
  
  createTypeProthese: (data: Partial<TypeProthese>): Promise<AxiosResponse<TypeProthese>> =>
    api.post('/prosthesis/types-protheses/', data),
  
  updateTypeProthese: (id: number, data: Partial<TypeProthese>): Promise<AxiosResponse<TypeProthese>> =>
    api.patch(`/prosthesis/types-protheses/${id}/`, data),
  
  deleteTypeProthese: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/prosthesis/types-protheses/${id}/`),

  // Commandes de prothèses
  getCommandes: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<CommandeProtheseList>>> =>
    api.get('/prosthesis/commandes/', { params }),
  
  getCommande: (id: number): Promise<AxiosResponse<CommandeProthese>> =>
    api.get(`/prosthesis/commandes/${id}/`),
  
  createCommande: (data: CreateCommandeProtheseData): Promise<AxiosResponse<CommandeProthese>> =>
    api.post('/prosthesis/commandes/', data),
  
  updateCommande: (id: number, data: Partial<CommandeProthese>): Promise<AxiosResponse<CommandeProthese>> =>
    api.patch(`/prosthesis/commandes/${id}/`, data),
  
  deleteCommande: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/prosthesis/commandes/${id}/`),
  
  getCommandesEnRetard: (): Promise<AxiosResponse<CommandeProtheseList[]>> =>
    api.get('/prosthesis/commandes/en_retard/'),
  
  getLivraisonsSemaine: (): Promise<AxiosResponse<CommandeProtheseList[]>> =>
    api.get('/prosthesis/commandes/livraisons_semaine/'),
  
  getDashboardStats: (): Promise<AxiosResponse<DashboardProthesisStats>> =>
    api.get('/prosthesis/commandes/dashboard_stats/'),
  
  ajouterSuivi: (commandeId: number, data: CreateSuiviLivraisonData): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/prosthesis/commandes/${commandeId}/ajouter_suivi/`, data),

  // Suivis de livraison
  getSuivis: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<SuiviLivraison>>> =>
    api.get('/prosthesis/suivis/', { params }),
  
  getSuivi: (id: number): Promise<AxiosResponse<SuiviLivraison>> =>
    api.get(`/prosthesis/suivis/${id}/`),
  
  createSuivi: (data: CreateSuiviLivraisonData): Promise<AxiosResponse<SuiviLivraison>> =>
    api.post('/prosthesis/suivis/', data),
  
  updateSuivi: (id: number, data: Partial<SuiviLivraison>): Promise<AxiosResponse<SuiviLivraison>> =>
    api.patch(`/prosthesis/suivis/${id}/`, data),
  
  deleteSuivi: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/prosthesis/suivis/${id}/`),
  
  getTimelineSuivis: (): Promise<AxiosResponse<SuiviLivraison[]>> =>
    api.get('/prosthesis/suivis/timeline/'),

  // Notifications
  getNotifications: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<NotificationProthese>>> =>
    api.get('/prosthesis/notifications/', { params }),
  
  getNotification: (id: number): Promise<AxiosResponse<NotificationProthese>> =>
    api.get(`/prosthesis/notifications/${id}/`),
  
  marquerNotificationLue: (id: number): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/prosthesis/notifications/${id}/marquer_lu/`),
  
  marquerToutesNotificationsLues: (): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/prosthesis/notifications/marquer_toutes_lues/'),
  
  getNotificationsNonLues: (): Promise<AxiosResponse<{ count: number; notifications: NotificationProthese[] }>> =>
    api.get('/prosthesis/notifications/non_lues/'),
};

// API Treatments
export const treatmentsApi = {
  getTreatments: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<Treatment>>> =>
    api.get('/treatments/treatments/', { params }),

  getTreatment: (id: number): Promise<AxiosResponse<Treatment>> =>
    api.get(`/treatments/treatments/${id}/`),
};

// ==================== API Dépenses ====================
export const depenseService = {
  // Catégories de dépenses
  getCategories: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<CategorieDepense>>> =>
    api.get('/depenses/categories/', { params }),

  getCategoriesSimple: (): Promise<AxiosResponse<PaginatedResponse<CategorieDepense>>> =>
    api.get('/depenses/categories/', { params: { simple: 'true' } }),

  /** Catégories actives uniquement — pour les listes de saisie */
  getCategoriesActives: (): Promise<AxiosResponse<PaginatedResponse<CategorieDepense>>> =>
    api.get('/depenses/categories/', { params: { actives_only: 'true', page_size: 100 } }),

  getCategorie: (id: number): Promise<AxiosResponse<CategorieDepense>> =>
    api.get(`/depenses/categories/${id}/`),

  createCategorie: (data: Partial<CategorieDepense>): Promise<AxiosResponse<CategorieDepense>> =>
    api.post('/depenses/categories/', data),

  updateCategorie: (id: number, data: Partial<CategorieDepense>): Promise<AxiosResponse<CategorieDepense>> =>
    api.put(`/depenses/categories/${id}/`, data),

  deleteCategorie: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/depenses/categories/${id}/`),

  toggleCategorieStatus: (
    id: number,
    isActive?: boolean
  ): Promise<AxiosResponse<{ message: string; is_active: boolean; categorie: CategorieDepense }>> =>
    api.post(
      `/depenses/categories/${id}/toggle_status/`,
      isActive === undefined ? {} : { is_active: isActive }
    ),

  getTypesCategorie: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/depenses/categories/types/'),

  getCategoriesWithStats: (): Promise<AxiosResponse<CategorieDepense[]>> =>
    api.get('/depenses/categories/with_stats/'),

  // Dépenses
  getDepenses: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<DepenseList>>> =>
    api.get('/depenses/depenses/', { params }),

  getDepense: (id: number): Promise<AxiosResponse<Depense>> =>
    api.get(`/depenses/depenses/${id}/`),

  createDepense: (data: CreateDepenseData): Promise<AxiosResponse<Depense>> =>
    api.post('/depenses/depenses/', data),

  updateDepense: (id: number, data: Partial<CreateDepenseData>): Promise<AxiosResponse<Depense>> =>
    api.put(`/depenses/depenses/${id}/`, data),

  deleteDepense: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/depenses/depenses/${id}/`),

  getStatistiques: (params?: { mois?: number; annee?: number }): Promise<AxiosResponse<DepenseStatistiques>> =>
    api.get('/depenses/depenses/statistiques/', { params }),

  getDepensesEnAttente: (): Promise<AxiosResponse<DepenseList[]>> =>
    api.get('/depenses/depenses/en_attente/'),

  getDepensesEnRetard: (): Promise<AxiosResponse<DepenseList[]>> =>
    api.get('/depenses/depenses/en_retard/'),

  getDepensesRecurrentes: (): Promise<AxiosResponse<DepenseList[]>> =>
    api.get('/depenses/depenses/recurrentes/'),

  approuverDepense: (id: number): Promise<AxiosResponse<{ message: string; depense: Depense }>> =>
    api.post(`/depenses/depenses/${id}/approuver/`),

  payerDepense: (id: number, data: PayerDepenseData): Promise<AxiosResponse<{ message: string; depense: Depense }>> =>
    api.post(`/depenses/depenses/${id}/payer/`, data),

  annulerDepense: (id: number, motif?: string): Promise<AxiosResponse<{ message: string; depense: Depense }>> =>
    api.post(`/depenses/depenses/${id}/annuler/`, { motif }),

  getModesPaiement: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/depenses/depenses/modes_paiement/'),

  getPriorites: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/depenses/depenses/priorites/'),

  getStatuts: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/depenses/depenses/statuts/'),

  // Budgets mensuels
  getBudgets: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<BudgetMensuel>>> =>
    api.get('/depenses/budgets/', { params }),

  getBudget: (id: number): Promise<AxiosResponse<BudgetMensuel>> =>
    api.get(`/depenses/budgets/${id}/`),

  getBudgetActuel: (): Promise<AxiosResponse<BudgetMensuel>> =>
    api.get('/depenses/budgets/actuel/'),

  createBudget: (data: Partial<BudgetMensuel>): Promise<AxiosResponse<BudgetMensuel>> =>
    api.post('/depenses/budgets/', data),

  updateBudget: (id: number, data: Partial<BudgetMensuel>): Promise<AxiosResponse<BudgetMensuel>> =>
    api.put(`/depenses/budgets/${id}/`, data),

  deleteBudget: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/depenses/budgets/${id}/`),

  // Paiements fournisseurs
  getPaiementsFournisseurs: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<PaiementFournisseurDepense>>> =>
    api.get('/depenses/paiements-fournisseurs/', { params }),

  getPaiementFournisseur: (id: number): Promise<AxiosResponse<PaiementFournisseurDepense>> =>
    api.get(`/depenses/paiements-fournisseurs/${id}/`),

  createPaiementFournisseur: (data: Partial<PaiementFournisseurDepense>): Promise<AxiosResponse<PaiementFournisseurDepense>> =>
    api.post('/depenses/paiements-fournisseurs/', data),

  getPaiementsParFournisseur: (): Promise<AxiosResponse<Array<{ fournisseur__id: number; fournisseur__nom: string; total: number; count: number }>>> =>
    api.get('/depenses/paiements-fournisseurs/par_fournisseur/'),
};

// ==================== API Factures Pro Forma ====================
export const proformaService = {
  // CRUD Proformas
  getProformas: (params?: QueryParams): Promise<AxiosResponse<PaginatedResponse<FactureProformaList>>> =>
    api.get('/facturation/proformas/', { params }),

  getProforma: (id: number): Promise<AxiosResponse<FactureProforma>> =>
    api.get(`/facturation/proformas/${id}/`),

  createProforma: (data: CreateProformaData): Promise<AxiosResponse<FactureProforma>> =>
    api.post('/facturation/proformas/', data),

  updateProforma: (id: number, data: Partial<CreateProformaData>): Promise<AxiosResponse<FactureProforma>> =>
    api.put(`/facturation/proformas/${id}/`, data),

  deleteProforma: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/facturation/proformas/${id}/`),

  // Statuts et listes
  getStatuts: (): Promise<AxiosResponse<Array<{ value: string; label: string }>>> =>
    api.get('/facturation/proformas/statuts/'),

  getValides: (): Promise<AxiosResponse<FactureProformaList[]>> =>
    api.get('/facturation/proformas/valides/'),

  getExpirees: (): Promise<AxiosResponse<FactureProformaList[]>> =>
    api.get('/facturation/proformas/expirees/'),

  // Actions
  envoyerProforma: (id: number): Promise<AxiosResponse<{ message: string; proforma: FactureProforma }>> =>
    api.post(`/facturation/proformas/${id}/envoyer/`),

  accepterProforma: (id: number): Promise<AxiosResponse<{ message: string; proforma: FactureProforma }>> =>
    api.post(`/facturation/proformas/${id}/accepter/`),

  refuserProforma: (id: number): Promise<AxiosResponse<{ message: string; proforma: FactureProforma }>> =>
    api.post(`/facturation/proformas/${id}/refuser/`),

  convertirProforma: (id: number, data: ConvertProformaData): Promise<AxiosResponse<{ message: string; facture_id: number; facture_numero: string }>> =>
    api.post(`/facturation/proformas/${id}/convertir/`, data),

  annulerProforma: (id: number): Promise<AxiosResponse<{ message: string; proforma: FactureProforma }>> =>
    api.post(`/facturation/proformas/${id}/annuler/`),

  dupliquerProforma: (id: number): Promise<AxiosResponse<{ message: string; proforma: FactureProforma }>> =>
    api.post(`/facturation/proformas/${id}/dupliquer/`),

  // Impression
  imprimerProforma: (id: number): Promise<AxiosResponse<Blob>> =>
    api.get(`/facturation/proformas/${id}/imprimer/`, { responseType: 'blob' }),

  // Statistiques
  getStatistiques: (): Promise<AxiosResponse<ProformaStatistiques>> =>
    api.get('/facturation/proformas/statistiques/'),
};
